/**
 * LISTA NEGRA (H103-D) — quem não fica nos grupos.
 *
 * ## O que esta tela nunca faz
 *
 * 1. **Não deixa achar que pôr na lista expulsa.** Ela bloqueia as PRÓXIMAS entradas; tirar quem já
 *    está dentro é o outro botão. Colar 200 números de uma planilha não pode virar 200 expulsões em
 *    70 grupos sem ninguém confirmar — e a única defesa contra isso é o texto dizer, antes. Por isso o
 *    aviso mora DENTRO do diálogo de pôr na lista, acima do botão.
 * 2. **Não promete o que não sabe.** *"Em quantos grupos esta pessoa está?"* — não sabemos, e não há
 *    cadastro de participantes. A tela diz **em quantos vamos tentar**, que são os que este número
 *    administra.
 * 3. **Não esconde recusa.** Expulsar volta por grupo; os que falharam aparecem com o motivo, e o
 *    mais comum tem conserto conhecido (não somos administradores daquele grupo).
 *
 * ## A família (spec 22)
 *
 * Consulta dentro do módulo de Grupos (`ModuloDeGrupos`, aba Lista negra) + `ListView`; "Pôr na lista" é a primária
 * da aba, no cabeçalho do módulo (o mesmo lugar da primária das outras abas). Pôr na lista é um formulário curto (`Dialogo`); o resultado
 * por grupo de uma expulsão abre na `Gaveta`.
 */

import { useId, useState, type ReactNode } from "react";
import { EstadoDaFalha, chamar, falhaDe, identificadorLegivel, textos } from "@hugi/ui";
import {
  Dialogo, Dica, Erro, Gaveta, ListView, SemPermissao, Vazio,
  type ColunaDaLista,
} from "@hugi/ui/kit";
import { ShieldBan } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import "./hugi-grupos.css";
import { ModuloDeGrupos } from "./modulo-de-grupos.js";

interface NaLista {
  readonly id: string;
  /** @cru */
  readonly identificador: string;
  readonly motivo: string | null;
  readonly criadoEm: string;
  readonly expulsoes: number;
  readonly recusas: number;
}

interface PorGrupo {
  readonly grupo: string;
  readonly resultado: "expulso" | "nao_estava" | "falhou" | "nao_somos_admin";
}

const CHAVE = ["lista-negra"] as const;

const TITULO = "Lista negra";
const FRASE = "Quem está aqui é tirado do grupo assim que entra.";

export function ListaNegra() {
  const clienteDeConsulta = useQueryClient();
  const consulta = useQuery({
    queryKey: CHAVE,
    queryFn: () => chamar<{ itens: readonly NaLista[]; gruposAdministrados: number }>("/api/painel/lista-negra"),
  });

  const [pondo, setPondo] = useState(false);
  const [numeros, setNumeros] = useState("");
  const [motivo, setMotivo] = useState("");
  const [expulsao, setExpulsao] = useState<{ readonly numero: string; readonly porGrupo: readonly PorGrupo[] } | null>(null);

  const recarregar = () => void clienteDeConsulta.invalidateQueries({ queryKey: CHAVE });

  const por = useMutation({
    mutationFn: () => chamar<{ postos: number; repetidos: number; invalidos: string[] }>("/api/painel/lista-negra", { metodo: "POST", corpo: { numeros, motivo } }),
    onSuccess: () => { setNumeros(""); setMotivo(""); recarregar(); },
  });

  const tirar = useMutation({
    mutationFn: (id: string) => chamar<Record<string, never>>(`/api/painel/lista-negra/${id}`, { metodo: "DELETE" }),
    onSuccess: recarregar,
  });

  const expulsar = useMutation({
    mutationFn: (n: NaLista) =>
      chamar<{ expulsos: number; falhados: number; semAdmin: number; porGrupo: PorGrupo[] }>(`/api/painel/lista-negra/${n.id}/expulsar`, { metodo: "POST", corpo: {} })
        // Este número vai para um TÍTULO ("Resultado por grupo — …"), não para a coluna em
        // fonte mono. Título é lugar de nome, e nome não é treze dígitos.
        .then((r) => ({ ...r, numero: identificadorLegivel(n.identificador) })),
    onSuccess: (r) => { setExpulsao({ numero: r.numero, porGrupo: r.porGrupo }); recarregar(); },
  });

  if (consulta.isError) {
    return (
      <ModuloDeGrupos ativa="lista-negra" primaria={<Acao tom="primaria" onClick={() => setPondo(true)}>Pôr na lista</Acao>}>
        <div className="v2-cartao"><Falha erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /></div>
      </ModuloDeGrupos>
    );
  }

  const administrados = consulta.data?.gruposAdministrados ?? 0;

  const colunas: ColunaDaLista<NaLista>[] = [
    { id: "numero", rotulo: "Número", principal: true, celula: (n) => <span className="hugi-mono">{identificadorLegivel(n.identificador)}</span>, ordenar: (n) => identificadorLegivel(n.identificador) },
    { id: "motivo", rotulo: "Motivo", celula: (n) => (n.motivo ? n.motivo : <span className="is-ausente">sem motivo anotado</span>), dica: (n) => n.motivo ?? undefined, ordenar: (n) => n.motivo },
    { id: "tirado", rotulo: "Tirado", numerica: true, celula: (n) => (n.expulsoes > 0 ? `${n.expulsoes}×` : "—"), ordenar: (n) => n.expulsoes },
    { id: "recusas", rotulo: "Recusas", numerica: true, celula: (n) => (n.recusas > 0 ? n.recusas : "—"), ordenar: (n) => n.recusas },
    { id: "criado", rotulo: "Posto em", celula: (n) => new Date(n.criadoEm).toLocaleDateString("pt-BR"), ordenar: (n) => n.criadoEm },
    {
      id: "acoes", rotulo: "Ações", acoes: true,
      celula: (n) => (
        <span className="hugi-acoes-da-linha">
          <Acao
            pequeno
            desabilitado={expulsar.isPending || administrados === 0}
            {...(administrados === 0 ? { motivo: "Este workspace não administra nenhum grupo." } : {})}
            onClick={() => expulsar.mutate(n)}
          >
            Tirar dos grupos ({administrados})
          </Acao>
          <Acao pequeno tom="perigo" desabilitado={tirar.isPending} onClick={() => tirar.mutate(n.id)}>Remover da lista</Acao>
        </span>
      ),
    },
  ];

  const falhaDaAcao = expulsar.isError ? expulsar.error : tirar.isError ? tirar.error : null;

  return (
    <ModuloDeGrupos ativa="lista-negra" primaria={<Acao tom="primaria" onClick={() => setPondo(true)}>Pôr na lista</Acao>}>
      <ListView<NaLista>
        titulo={TITULO}
        icone={<ShieldBan size={18} />}
        colunas={colunas}
        linhas={consulta.data?.itens as NaLista[] | undefined}
        chave={(n) => n.id}
        memoria="hugi:lista-negra"
        busca={{ campos: (n) => [n.identificador, n.motivo], placeholder: "Número ou motivo" }}
        contagem={(n) => (n === 1 ? "1 número" : `${n} números`)}
        vazio={{
          semNada: <><strong>Ninguém na lista</strong>{" "}<span>Ponha os números que não devem ficar nos seus grupos. Quando um deles entrar, é tirado antes de ler qualquer coisa — desde que este número administre o grupo.</span></>,
        }}
        acoes={<Dica texto={FRASE} />}
        {...(falhaDaAcao ? { avisos: <span className="v2-erro" role="alert"> · {mensagemDe(falhaDaAcao)}</span> } : {})}
      />

      <Dialogo
        aberto={pondo}
        aoFechar={() => setPondo(false)}
        titulo="Pôr na lista negra"
        rodape={
          <>
            <Acao onClick={() => setPondo(false)}>Fechar</Acao>
            <Acao
              tom="primaria"
              desabilitado={por.isPending || numeros.trim() === ""}
              {...(numeros.trim() === "" ? { motivo: "Escreva pelo menos um número." } : {})}
              onClick={() => por.mutate()}
            >
              Pôr na lista
            </Acao>
          </>
        }
      >
        <div className="hugi-grupos-formulario">
          <p role="note" className="hugi-grupos-aviso">
            Pôr na lista bloqueia as <strong>próximas</strong> entradas. Quem já está dentro continua dentro — para tirar, use “Tirar dos grupos” em cada número.
          </p>
          <label className="hugi-grupos-campo">
            <span>Números</span>
            <textarea
              className="campo hugi-mono"
              rows={4}
              placeholder="5527999990001, 5527999990002…  (pode colar de uma planilha)"
              value={numeros}
              onChange={(e) => setNumeros(e.target.value)}
            />
          </label>
          <label className="hugi-grupos-campo">
            <span>Motivo <small>(fica no registro, ninguém do grupo vê)</small></span>
            <input className="campo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="spam, concorrente, golpe…" />
          </label>
          {por.data ? (
            <p role="status" className="hugi-grupos-nota">
              {por.data.postos} posto(s){por.data.repetidos > 0 ? `, ${por.data.repetidos} já estava(m)` : ""}
              {por.data.invalidos.length > 0 ? ` · ignorados: ${por.data.invalidos.join(", ")}` : ""}
            </p>
          ) : null}
          {por.isError ? <p className="v2-erro" role="alert">{mensagemDe(por.error)}</p> : null}
        </div>
      </Dialogo>

      <Gaveta aberta={expulsao !== null} aoFechar={() => setExpulsao(null)} titulo={expulsao ? `Resultado por grupo — ${expulsao.numero}` : "Resultado por grupo"}>
        {expulsao ? (
          expulsao.porGrupo.length === 0 ? (
            <Vazio>Nenhum grupo respondeu.</Vazio>
          ) : (
            <ul className="hugi-grupos-resultado">
              {expulsao.porGrupo.map((g, i) => (
                <li key={`${g.grupo}-${i}`}>
                  <span title={g.grupo}>{g.grupo}</span>
                  <span className={g.resultado === "expulso" ? undefined : "is-ausente"}>
                    {g.resultado === "expulso" ? "tirado" : g.resultado === "nao_somos_admin" ? "não administramos" : "não saiu"}
                  </span>
                </li>
              ))}
            </ul>
          )
        ) : null}
      </Gaveta>
    </ModuloDeGrupos>
  );
}

// --- Miúdos da tela ---------------------------------------------------------------------------------

/** A frase do servidor quando ele deu uma; sem ela, a genérica. */
function mensagemDe(erro: unknown): string {
  return falhaDe(erro)?.erro.mensagem ?? textos.estados.erro.corpo;
}

/** O estado de falha da área, com os estados do kit (e o da casca quando é caso comercial ou de versão). */
function Falha(props: { readonly erro: unknown; readonly aoTentarDeNovo?: () => void }) {
  const f = falhaDe(props.erro);
  if (f?.tipo === "sem_permissao") return <SemPermissao>{f.permissao ? textos.estados.semPermissao.qual(f.permissao) : textos.estados.semPermissao.semNome}</SemPermissao>;
  if (f?.tipo === "sem_entitlement" || f?.tipo === "versao_incompativel") return <EstadoDaFalha falha={f} />;
  return <Erro {...(props.aoTentarDeNovo ? { aoTentarDeNovo: props.aoTentarDeNovo } : {})}>{f?.erro.mensagem ?? textos.estados.erro.corpo}</Erro>;
}

/** Botão do kit; desabilitado, diz o motivo (no `title` e ligado por `aria-describedby`). */
function Acao(props: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly tom?: "primaria" | "perigo";
  readonly pequeno?: boolean;
  readonly desabilitado?: boolean;
  readonly motivo?: string;
}) {
  const id = useId();
  const comMotivo = Boolean(props.desabilitado && props.motivo);
  return (
    <>
      <button
        type="button"
        className={`v2-botao${props.tom === "primaria" ? " is-primario" : props.tom === "perigo" ? " is-perigo" : ""}${props.pequeno ? " is-pequeno" : ""}`}
        disabled={props.desabilitado}
        title={comMotivo ? props.motivo : undefined}
        aria-describedby={comMotivo ? id : undefined}
        onClick={props.onClick}
      >
        {props.children}
      </button>
      {comMotivo ? <span id={id} className="v2-sr">{props.motivo}</span> : null}
    </>
  );
}
